import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { CODES } from "../../../consts/codes";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Componente ModalInfo para gestión de usuarios.
 */
const ModalInfo = ({
  title,
  responseData,
  onClose,
  open,
  hideResponseMessage,
  close = true,
  className = "",
  classNameTitle = "",
  customIcon,
  onAccept,
  acceptButtonStyles,
  buttonText = "",
  disableDefaultMaxWidth,
  customPaperSx,
  additionalMessage = "",
}) => {
  const { data, status } = responseData;
  /** Maneja el cierre del modal. */
  const handleClose = () => {
    onClose();
  };

  let titleColor = "";
  if (status === CODES.COD_RESPONSE_HTTP_OK) {
    if (
      data.responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST ||
      data.responseCode === CODES.COD_RESPONSE_SUCCESS ||
      data.responseCode === CODES.COD_RESPONSE_HTTP_OK ||
      data.responseCode === CODES.COD_RESPONSE_HTTP_CREATED
    ) {
      titleColor = "success";
    } else if (
      data.responseCode === CODES.COD_RESPONSE_ERROR_RESET_PASSWORD ||
      data.responseCode === CODES.COD_RESPONSE_ERROR_GENERATE_FILE
    ) {
      titleColor = "warningPassword";
    } else if (
      data.responseCode === CODES.COD_RESPONSE_ERROR_LOGIN ||
      data.responseCode === CODES.COD_RESPONSE_ERROR_UPDATE ||
      data.responseCode === CODES.COD_LIMIT_EXCEEDED
    ) {
      titleColor = "warning";
    } else {
      titleColor = "error";
    }
  } else if (status === CODES.COD_RESPONSE_HTTP_ERROR) {
    if (data.responseCode === CODES.COD_RESPONSE_ERROR_UPDATE) {
      titleColor = "warning";
    } else {
      titleColor = "error";
    }
  } else if (
    status === CODES.COD_RESPONSE_ERROR_RESET_PASSWORD ||
    status === CODES.COD_RESPONSE_ERROR_GENERATE_FILE
  ) {
    titleColor = "warningPassword";
  } else {
    titleColor = "warning";
  }

  const { t } = useTranslation();

  /** Componente que envuelve el título del diálogo con botón de cierre. */
  const DialogTitleContainer = (props) => {
    const { children, onClose, ...other } = props;

    return (
      <DialogTitle {...other}>
        {children}
        {onClose && close ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon sx={{ color: "#543ab4" }} />
          </IconButton>
        ) : null}
      </DialogTitle>
    );
  };

  /** Renderiza el mensaje de error con enlaces detectados. */
  const errorMessage = () => {
    const regex = /https?:\/\/[^\s]+/g;
    if (typeof data.responseMessage == "string") {
      const matchedUrls = data.responseMessage.match(regex);
      let message = data.responseMessage;
      let messageLink = "";

      if (matchedUrls && matchedUrls.length > 0) {
        messageLink = matchedUrls[0];
        message = data.responseMessage.replace(matchedUrls[0], "").trim();
        return (
          <p className={className || "regular-text_black"}>
            {message}{" "}
            <a
              href={messageLink}
              target="_blank"
              rel="noopener noreferrer"
              className="regular-text_black_link"
            >
              {messageLink}
            </a>
          </p>
        );
      } else {
        return <p className={className || "regular-text_black"}>{data.responseMessage}</p>;
      }
    }
  };

  return (
    <Grid>
      <Dialog
        className="modal__notification"
        onClose={handleClose}
        open={open}
        aria-labelledby="customized-dialog-title"
        sx={
          customPaperSx
            ? { "& .MuiPaper-root": { ...customPaperSx } } // 🔥 solo aplica si existe la prop
            : undefined // si no se pasa, no cambia nada
        }
      >
        <Grid
          style={{
            padding: "2rem",
          }}
          className="modal__infoWidth"
        >
          <DialogTitleContainer
            onClose={handleClose}
            sx={{
              padding: {
                xl: "1.6rem 2.4rem",
                lg: "1.6rem 2.4rem",
                sm: "1.6rem 2.4rem",
                xs: "1.6rem 0rem",
              },
            }}
          >
            {customIcon ? (
              customIcon
            ) : (
              <>
                {titleColor === "success" && (
                  <TaskAltOutlinedIcon sx={{ color: "#60CC64", fontSize: "5.5rem" }} />
                )}
                {titleColor === "warning" && (
                  <InfoOutlinedIcon sx={{ color: "#4dcafa", fontSize: "5.5rem" }} />
                )}
                {titleColor === "warningPassword" && (
                  <WarningAmberOutlinedIcon sx={{ color: "#ffa836", fontSize: "5.5rem" }} />
                )}
                {titleColor === "error" && (
                  <WarningAmberOutlinedIcon sx={{ color: "#4dcafa", fontSize: "5.5rem" }} />
                )}
              </>
            )}
          </DialogTitleContainer>

          <DialogContent className="modal-info-title">
            <Grid
              className="modal-info-title-text full-width"
              style={{ whiteSpace: "pre-line", minWidth: "220px" }}
            >
              {titleColor === "success" && (
                <label className={classNameTitle}>
                  {title ? title : t("App.validationMessages.success")}
                </label>
              )}
              {titleColor === "warningPassword" && (
                <label className={classNameTitle}>
                  {title ? title : t("App.managePortalUsers.modal.modalExist")}
                </label>
              )}

              {titleColor === "error" && (
                <label className={classNameTitle}>
                  {title ? title : t("App.validationMessages.error")}
                </label>
              )}
              {titleColor === "warning" && <label className={classNameTitle}>{" " + title}</label>}
            </Grid>
            {!hideResponseMessage && data?.responseMessage && (
              <Grid className="modal-info-title-text full-width">
                <p className={className || "regular-text_black full-width"}>
                  {titleColor === "warning" || titleColor === "error"
                    ? errorMessage()
                    : data?.data || data?.responseMessage?.message || data?.responseMessage}
                </p>
              </Grid>
            )}
            {additionalMessage && (
              <Grid className="modal-info-title-text full-width">
                <p className={className || "regular-text__primary-one full-width"}>
                  {additionalMessage}
                </p>
              </Grid>
            )}
          </DialogContent>

          <DialogActions>
            <Grid item xl={3} lg={3} md={3} sm={3} xs={12}>
              <Button
                variant="contained"
                className="btn__filled__blue dimentions-btn__applyFilter"
                sx={{
                  acceptButtonStyles,
                }}
                onClick={() => {
                  if (onAccept) {
                    onAccept();
                  }
                  handleClose();
                }}
              >
                {buttonText || t("App.buttonLabels.accept")}
              </Button>
            </Grid>
          </DialogActions>
        </Grid>
      </Dialog>
    </Grid>
  );
};

export default ModalInfo;
